import { IconChevronLeft, IconChevronRight } from '@/theme/icons';
import { isThisMonth } from '@/utils/calendar';
import { getDateFnsLocale } from '@/utils/dateLocale';
import rem from '@/utils/rem';
import {
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { addMonths, subMonths } from 'date-fns';
import { useCallback } from 'react';
import { Navigate } from 'react-big-calendar';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';

import InnerDate from './innerDate';

type Props = {
  selectedDate: Date;
  updateSelectedDate: (date: Date) => void;
  showCalendarIcon?: boolean;
  showDatepicker?: boolean;
  onCategorySpotlight?: boolean;
};

const MonthYearNavigator = ({
  selectedDate,
  updateSelectedDate,
  showCalendarIcon,
  showDatepicker = false,
  onCategorySpotlight = false,
}: Props) => {
  const { t, i18n } = useTranslation('MonthYearNavigator');
  const {
    isOpen: isDatePickerOpen,
    onOpen: onDatePickerOpen,
    onClose: onDatePickerClose,
  } = useDisclosure({ id: 'datepicker-month-year-navigation' });

  const locale = getDateFnsLocale(i18n.language);

  // Color logic for Category Spotlight
  const getBorderColor = () => {
    if (!onCategorySpotlight) return 'stroke-primary';

    const currentDate = new Date();
    const isCurrentMonth = isThisMonth(selectedDate);
    const isFutureMonth = selectedDate > currentDate;

    if (isCurrentMonth) return 'green.400'; // Current month - green
    if (isFutureMonth) return 'yellow.400'; // Future month - yellow
    return 'red.400'; // Past month - red
  };

  const onHandlePrevNextCalendarNavigation = useCallback(
    (date: Date, action: 'PREV' | 'NEXT') => {
      switch (action) {
        case Navigate.PREVIOUS: {
          const newDate = subMonths(date, 1);
          updateSelectedDate(newDate);

          return newDate;
        }

        case Navigate.NEXT: {
          const newDate = addMonths(date, 1);
          updateSelectedDate(newDate);

          return newDate;
        }

        default: {
          updateSelectedDate(date);
          return date;
        }
      }
    },
    [updateSelectedDate],
  );

  const onSelectedDate = useCallback(
    (selectedDate: Date) => {
      updateSelectedDate(selectedDate);
      onDatePickerClose();
    },
    [onDatePickerClose, updateSelectedDate],
  );

  if (showDatepicker) {
    return (
      <HStack
        position="relative"
        justifyContent="center"
        gap={rem(5)}
        width="fit-content"
        minWidth="fit-content"
        height="full"
        paddingInlineEnd={rem(8)}
        paddingInlineStart={rem(8)}
      >
        <Tooltip label={t('tooltips.previousMonth')} placement="top">
          <IconButton
            width={rem(32)}
            minWidth={0}
            height="full"
            margin={0}
            padding={0}
            borderWidth={rem(1)}
            borderStyle="solid"
            borderColor="stroke-primary"
            borderRadius={rem(4)}
            aria-label={t('buttons.previousMonth')}
            backgroundColor="background-primary"
            icon={<Icon as={IconChevronLeft} boxSize={rem(14)} />}
            onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, Navigate.PREVIOUS)}
            size="xs"
            variant="ghost"
          />
        </Tooltip>

        <Popover closeOnBlur closeOnEsc isLazy isOpen={isDatePickerOpen} onClose={onDatePickerClose} placement="bottom">
          <PopoverTrigger>
            <InnerDate
              selectedDate={selectedDate}
              showCalendarIcon={showCalendarIcon}
              showDatePicker={showDatepicker}
              onDatePickerOpen={onDatePickerOpen}
              borderColor={getBorderColor()}
            />
          </PopoverTrigger>
          <PopoverContent
            width={rem(230)}
            height={rem(200)}
            padding={rem(16)}
            border={`${rem(1)} solid`}
            borderColor="stroke-primary"
            borderRadius={rem(4)}
            backgroundColor="background-primary"
          >
            <PopoverBody className="month-style" padding={0}>
              <DatePicker selected={selectedDate} onChange={onSelectedDate} showMonthYearPicker open locale={locale} />
            </PopoverBody>
          </PopoverContent>
        </Popover>

        <Tooltip label={t('tooltips.nextMonth')} placement="top">
          <IconButton
            width={rem(32)}
            minWidth={0}
            height="full"
            margin={0}
            padding={0}
            borderWidth={rem(1)}
            borderStyle="solid"
            borderColor="stroke-primary"
            borderRadius={rem(4)}
            aria-label={t('buttons.nextMonth')}
            backgroundColor="background-primary"
            icon={<Icon as={IconChevronRight} boxSize={rem(14)} />}
            onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, Navigate.NEXT)}
            size="xs"
            variant="ghost"
          />
        </Tooltip>
      </HStack>
    );
  }

  return (
    <HStack
      position="relative"
      justifyContent="center"
      gap={rem(5)}
      width="fit-content"
      minWidth="fit-content"
      height="full"
      paddingInlineEnd={rem(8)}
      paddingInlineStart={rem(8)}
    >
      <Tooltip label={t('tooltips.previousMonth')} placement="top">
        <IconButton
          width={rem(32)}
          minWidth={0}
          height="full"
          margin={0}
          padding={0}
          borderWidth={rem(1)}
          borderStyle="solid"
          borderColor="stroke-primary"
          borderRadius={rem(4)}
          aria-label={t('buttons.previousMonth')}
          aspectRatio={1}
          backgroundColor="background-primary"
          icon={<Icon as={IconChevronLeft} boxSize={rem(14)} />}
          onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, Navigate.PREVIOUS)}
          size="xs"
          variant="ghost"
        />
      </Tooltip>

      <InnerDate
        selectedDate={selectedDate}
        showCalendarIcon={showCalendarIcon}
        showDatePicker={showDatepicker}
        borderColor={getBorderColor()}
      />

      <Tooltip label={t('tooltips.nextMonth')} placement="top">
        <IconButton
          width={rem(32)}
          minWidth={0}
          height="full"
          margin={0}
          padding={0}
          borderWidth={rem(1)}
          borderStyle="solid"
          borderColor="stroke-primary"
          borderRadius={rem(4)}
          aria-label={t('buttons.nextMonth')}
          aspectRatio={1}
          backgroundColor="background-primary"
          icon={<Icon as={IconChevronRight} boxSize={rem(14)} />}
          onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, Navigate.NEXT)}
          size="xs"
          variant="ghost"
        />
      </Tooltip>
    </HStack>
  );
};

export default MonthYearNavigator;
